import React, { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { BookOpenIcon, ChevronDownIcon, IdCardIcon, KeyRoundIcon, NfcIcon, PencilIcon, PrinterIcon, SparklesIcon, VolleyballIcon, WalletIcon, XIcon, BoxIcon } from "lucide-react";
import { Logo } from "../Logo";
import { CardNumber } from "./CardNumber";
import { citizen, familyMembers, walletCards, FamilyMember } from "../../data/carte";
const ease = [0.23, 1, 0.32, 1] as const;
const cardIcons: Record<string, BoxIcon> = {
  citoyenne: IdCardIcon,
  bibliotheque: BookOpenIcon,
  loisirs: VolleyballIcon
};
const iconButton = 'grid h-12 w-12 place-items-center rounded-full border border-slate-200 text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo';
interface DigitalCardProps {
  memberId: string;
  onEdit: (member: FamilyMember) => void;
}
export function DigitalCard({
  memberId,
  onEdit
}: DigitalCardProps) {
  const [nfcActive, setNfcActive] = useState(false);
  const [otpOpen, setOtpOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [cardsOpen, setCardsOpen] = useState(false);
  const [activeId, setActiveId] = useState(walletCards[0].id);
  const holder = familyMembers.find(member => member.id === memberId) ?? familyMembers[0];
  const activeIndex = walletCards.findIndex(card => card.id === activeId);
  const active = walletCards[activeIndex];
  const others = walletCards.filter(card => card.id !== activeId);
  const ActiveIcon = cardIcons[active.id];
  const memberIndex = familyMembers.findIndex(member => member.id === holder.id);
  const directionRef = useRef(1);
  const previousMember = useRef(memberIndex);
  if (memberIndex !== previousMember.current) {
    directionRef.current = memberIndex > previousMember.current ? 1 : -1;
    previousMember.current = memberIndex;
  }
  const direction = directionRef.current;
  const selectCard = (id: string) => {
    const nextIndex = walletCards.findIndex(card => card.id === id);
    directionRef.current = nextIndex > activeIndex ? 1 : -1;
    setActiveId(id);
    setCardsOpen(false);
  };
  return <section aria-labelledby="carte-numerique-titre" className="flex flex-col" data-id="element-615" data-mp="37got8niwm7wz">
      <h2 id="carte-numerique-titre" className="sr-only" data-id="element-616" data-mp="32h2hkiiq4zqa">
        Carte numérique
      </h2>

      <div className="relative" data-id="element-617" data-mp="2xqcbt46dyuv2">
        <AnimatePresence initial={false} mode="popLayout" custom={direction} data-id="element-618" data-mp="2xq3w74ivpbg6">
          <motion.div key={`${holder.id}-${active.id}`} custom={direction} initial={{
          x: direction * 360,
          opacity: 0,
          zIndex: 2
        }} animate={{
          x: 0,
          opacity: 1,
          zIndex: 2
        }} exit={{
          x: direction * -24,
          scale: 0.97,
          opacity: 0,
          zIndex: 1
        }} transition={{
          duration: 0.55,
          ease
        }} className="relative rounded-3xl bg-white shadow-xl shadow-ville-navy/10 ring-1 ring-slate-200" data-id="element-619" data-mp="2sqzoyjfilnsh">
            <div className="relative flex items-center justify-between gap-3 rounded-t-3xl bg-ville-navy px-6 py-3.5" data-id="element-620" data-mp="z7hamhmsvd0x">
              <p className="flex items-center gap-2.5 text-[17px] font-bold tracking-tight text-white" data-id="element-621" data-mp="148502yvdrlt1">
                <ActiveIcon className="h-5 w-5" aria-hidden="true" data-id="element-622" data-mp="188oy04ns2tg8" />
                {active.label}
              </p>

              <div className="relative" data-id="element-623" data-mp="bgv4ivwcw2tk">
                <button type="button" onClick={() => setCardsOpen(v => !v)} aria-expanded={cardsOpen} className="inline-flex items-center gap-1 rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold text-white transition-colors duration-150 ease-ville hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-624" data-mp="gh9bsbq4v8qj">
                  +{others.length} cartes
                  <motion.span animate={{
                  rotate: cardsOpen ? 180 : 0
                }} transition={{
                  duration: 0.2,
                  ease
                }} data-id="element-625" data-mp="3sz7itgfm90q0">
                    <ChevronDownIcon className="h-4 w-4" aria-hidden="true" data-id="element-626" data-mp="34vztjdy7ym4e" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-627" data-mp="3jlp7k9reukm3">
                  {cardsOpen && <motion.ul initial={{
                  opacity: 0,
                  y: -6,
                  scale: 0.98
                }} animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  y: -6
                }} transition={{
                  duration: 0.18,
                  ease
                }} className="absolute right-0 top-full z-40 mt-2 w-60 overflow-hidden rounded-2xl bg-white p-1.5 shadow-xl ring-1 ring-slate-200" data-id="element-628" data-mp="3jmgwj1qi5k3e">
                      {others.map(card => {
                    const Icon = cardIcons[card.id];
                    return <li key={card.id} data-id="element-629" data-mp="237p9z5g1f720">
                            <button type="button" onClick={() => selectCard(card.id)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors duration-150 ease-ville hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-630" data-mp="iod4gc82t83a">
                              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-631" data-mp="dxn114j7nfb8">
                                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-632" data-mp="3w26q5e337d09" />
                              </span>
                              <span className="min-w-0" data-id="element-633" data-mp="410hcj2meuas">
                                <span className="block text-sm font-bold text-ville-navy" data-id="element-634" data-mp="3m5llituzc3wy">{card.label}</span>
                                <span className="block font-mono text-xs text-slate-500" data-id="element-635" data-mp="3r3dc9kjw1jzk">{card.number}</span>
                              </span>
                            </button>
                          </li>;
                  })}
                    </motion.ul>}
                </AnimatePresence>
              </div>
            </div>

            <div className="px-7 pb-7 pt-7 text-center" data-id="element-636" data-mp="1nx7mqt541ies">
              <div className="flex items-center gap-4 text-left" data-id="element-637" data-mp="1iw0eyu2ywj84">
                <button type="button" onClick={() => setPhotoOpen(true)} aria-label="Agrandir la photo" className="shrink-0 rounded-full transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-638" data-mp="1iymimimqmel2">
                  <img src={holder.photo} alt={`${holder.firstName} ${holder.lastName}`} className="h-20 w-20 rounded-full object-cover shadow-lg ring-4 ring-white" data-id="element-639" data-mp="26nyrtii2emnd" />
                </button>
                <p className="min-w-0 flex-1 text-2xl font-extrabold leading-tight tracking-tight text-ville-navy" data-id="element-640" data-mp="1wu89kvcj78g4">
                  {holder.firstName} {holder.lastName}
                </p>
                <button type="button" onClick={() => onEdit(holder)} aria-label={`Modifier la carte de ${holder.firstName}`} className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-slate-200 text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:bg-ville-indigo-soft hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-641" data-mp="2peovh9fgmdha">
                  <PencilIcon className="h-4 w-4" aria-hidden="true" data-id="element-642" data-mp="36uaunpge45vt" />
                </button>
              </div>

              <div className="mt-6 flex justify-center" data-id="element-643" data-mp="3btxckc75yaay">
                <Logo data-id="element-644" data-mp="3brb5edo6pwt9" />
              </div>

              <button type="button" onClick={() => setQrOpen(true)} aria-label="Agrandir le code QR" className="mt-5 inline-block rounded-2xl border border-slate-200 p-3 transition-[transform,border-color] duration-150 ease-ville hover:-translate-y-0.5 hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-645" data-mp="3fuhahhzk9hy5">
                <img src={`https://api.qrserver.com/v1/create-qr-code/?size=170x170&data=BEACONSFIELD-${holder.id}-${active.number}`} alt={`Code QR de la carte ${active.label}`} className="h-36 w-36" data-id="element-646" data-mp="3uhsixb0g4lu1" />
              </button>

              <div className="mt-4" data-id="element-647" data-mp="jpfxr90gnovj">
                <CardNumber value={active.number} data-id="element-648" data-mp="joefajh4803s" />
              </div>

              <p className="mt-4" data-id="element-649" data-mp="xpqs4m0zbllg">
                <span className="inline-flex rounded-full bg-ville-navy px-6 py-2 text-sm font-bold text-white" data-id="element-650" data-mp="xnxgstff8d74">
                  {active.badge}
                </span>
              </p>

              <div className="mt-7 flex items-center justify-center gap-3" data-id="element-651" data-mp="51l67vy6vqk1">
                <button type="button" aria-label="Imprimer ma carte" className={iconButton} data-id="element-652" data-mp="zhs0zk4y7v2">
                  <PrinterIcon className="h-5 w-5" aria-hidden="true" data-id="element-653" data-mp="eq35u9uqfrm3" />
                </button>
                <button type="button" aria-label="Ajouter au portefeuille" className={iconButton} data-id="element-654" data-mp="9zwg26ohi129">
                  <WalletIcon className="h-5 w-5" aria-hidden="true" data-id="element-655" data-mp="3if6ft84wiu6v" />
                </button>
                <button type="button" aria-label="Activer le partage NFC" aria-pressed={nfcActive} onClick={() => setNfcActive(v => !v)} className={nfcActive ? 'grid h-12 w-12 place-items-center rounded-full bg-ville-indigo text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2' : iconButton} data-id="element-656" data-mp="3dgmpywn6sy98">
                  <NfcIcon className="h-5 w-5" aria-hidden="true" data-id="element-657" data-mp="1jz7bs7pc9x04" />
                </button>
                <button type="button" aria-label="Générer un code à usage unique" onClick={() => setOtpOpen(true)} className={iconButton} data-id="element-658" data-mp="1oxii48gfdpdl">
                  <KeyRoundIcon className="h-5 w-5" aria-hidden="true" data-id="element-659" data-mp="2gnwsvvt56ll1" />
                </button>
              </div>
              {nfcActive && <p className="mt-2 text-xs font-semibold text-ville-indigo" data-id="element-660" data-mp="22ox3un1pw4oc">NFC activé · 60 s</p>}

              <p className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500" data-id="element-661" data-mp="27mp3y4cimx3m">
                <SparklesIcon className="h-4 w-4 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-662" data-mp="r8ffla89x8cs" />
                {active.note}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-4 flex justify-center gap-1" data-id="element-663" data-mp="3sfpn5iyfx4i7">
        {walletCards.map(card => <button key={card.id} type="button" onClick={() => selectCard(card.id)} aria-label={`Afficher la carte ${card.label}`} aria-current={card.id === activeId} className="group grid h-6 place-items-center px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-664" data-mp="18d3mxa34ez4">
            <span className={`h-2 rounded-full transition-all duration-200 ease-ville ${card.id === activeId ? 'w-6 bg-ville-indigo' : 'w-2 bg-slate-300 group-hover:bg-ville-indigo'}`} data-id="element-665" data-mp="39n3v79qnly2w" />
          </button>)}
      </div>

      <Overlay open={qrOpen} onClose={() => setQrOpen(false)} label="Code QR agrandi" data-id="element-666" data-mp="2zradlby2vee9">
        <h3 className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-667" data-mp="34pln1lkgnmvy">{active.label}</h3>
        <p className="mt-1 text-sm text-slate-600" data-id="element-668" data-mp="xznqvml7y5nl">Présentez ce code au comptoir ou à l’entrée.</p>
        <img src={`https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=BEACONSFIELD-${holder.id}-${active.number}`} alt="Code QR agrandi" className="mx-auto mt-5 h-60 w-60" data-id="element-669" data-mp="12w4hcnfw4w3u" />
        <div className="mt-4" data-id="element-670" data-mp="2ihg5lv9web6a">
          <CardNumber value={active.number} data-id="element-671" data-mp="2ii7zn1nuk0ug" />
        </div>
      </Overlay>

      <Overlay open={otpOpen} onClose={() => setOtpOpen(false)} label="Code à usage unique" data-id="element-672" data-mp="2x62vgqx5ilbr">
        <h3 className="text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-673" data-mp="2sdjfjnazre3s">Code à usage unique</h3>
        <p className="mt-1 text-sm text-slate-600" data-id="element-674" data-mp="364xxrrx2rb9s">Généré à l’instant pour un seul usage.</p>
        <img src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=BEACONSFIELD-OTC-8241" alt="Code QR à usage unique" className="mx-auto mt-5 h-40 w-40" data-id="element-675" data-mp="315uvugce5l61" />
        <p className="mt-4 font-mono text-2xl tracking-[0.3em] text-ville-navy" data-id="element-676" data-mp="3b5bz6r467myu">8241</p>
        <p className="mt-2 text-xs text-slate-500" data-id="element-677" data-mp="1lq4gy7j7f735">Valide 5 minutes · un seul usage</p>
      </Overlay>

      <Overlay open={photoOpen} onClose={() => setPhotoOpen(false)} label="Photo du titulaire" data-id="element-678" data-mp="1gr1bwn3850v6">
        <img src={holder.photo} alt={`${holder.firstName} ${holder.lastName}`} className="mx-auto h-64 w-64 rounded-2xl object-cover" data-id="element-679" data-mp="26f6qnidjgd7w" />
        <p className="mt-4 text-lg font-extrabold tracking-tight text-ville-navy" data-id="element-680" data-mp="2ahtl7r1lgc8s">
          {holder.firstName} {holder.lastName}
        </p>
        <p className="text-sm text-slate-500" data-id="element-681" data-mp="2fhy5ls5mhr4a">Ville de {citizen.city}</p>
      </Overlay>
    </section>;
}
interface OverlayProps {
  open: boolean;
  onClose: () => void;
  label: string;
  children: React.ReactNode;
}
function Overlay({
  open,
  onClose,
  label,
  children
}: OverlayProps) {
  return <AnimatePresence data-id="element-682" data-mp="2tdjnlfdfvan7">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-683" data-mp="2y6mne58t7gbu">
          <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-684" data-mp="386dhw6xddvbx" />
          <motion.div role="dialog" aria-modal="true" aria-label={label} initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative w-full max-w-sm rounded-t-3xl bg-white p-7 text-center shadow-2xl sm:rounded-3xl" data-id="element-685" data-mp="3d199x72lwip1">
            <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-686" data-mp="1k7b0rk3o5gq1">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-687" data-mp="15h3rnuzjxa20" />
            </button>
            {children}
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}